import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PerformanceTable = ({ data }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (actorId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(actorId)) {
      newExpanded?.delete(actorId);
    } else {
      newExpanded?.add(actorId);
    }
    setExpandedRows(newExpanded);
  };

  const getQualityColor = (score) => {
    if (score >= 90) return 'text-success';
    if (score >= 75) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-8">
      <h2 className="text-2xl font-bold text-card-foreground mb-6">Detailed Performance Statistics</h2>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Voice Actor</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Lines Recorded</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Quality Score</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Completion</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Followers</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Sessions</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Avg Duration</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Status</th>
              <th className="text-center py-3 px-4 text-sm font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data?.map((actor) => (
              <React.Fragment key={actor?.id}>
                <tr className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        {actor?.headshotUrl ? (
                          <img
                            src={actor?.headshotUrl}
                            alt={`${actor?.stageName} headshot`}
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <Icon name="User" size={20} className="text-primary" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-card-foreground">{actor?.stageName}</p>
                        <p className="text-xs text-muted-foreground">{actor?.vocalRange || 'N/A'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-card-foreground">
                      {actor?.totalLinesRecorded} / {actor?.totalLinesAssigned}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`font-bold ${getQualityColor(actor?.avgQualityScore || 0)}`}>
                      {actor?.avgQualityScore || 0}/100
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className="font-semibold text-card-foreground">{actor?.completionPercentage}%</span>
                      <div className="w-16 bg-muted rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            actor?.completionPercentage >= 80
                              ? 'bg-success'
                              : actor?.completionPercentage >= 50
                              ? 'bg-warning' :'bg-error'
                          }`}
                          style={{ width: `${actor?.completionPercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <Icon name="Users" size={14} className="text-accent" />
                      <span className="font-semibold text-card-foreground">{actor?.followerCount || 0}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-card-foreground">{actor?.recordingSessions || 0}</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-card-foreground">{actor?.avgSessionDuration || 0} min</span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex flex-col gap-1 items-center">
                      <span className="text-xs text-success font-medium">✓ {actor?.audioFileStatuses?.completed || 0}</span>
                      <span className="text-xs text-warning font-medium">⏳ {actor?.audioFileStatuses?.pending || 0}</span>
                      <span className="text-xs text-error font-medium">✗ {actor?.audioFileStatuses?.rejected || 0}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <button
                      onClick={() => toggleRow(actor?.id)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                    >
                      <Icon
                        name={expandedRows?.has(actor?.id) ? 'ChevronUp' : 'ChevronDown'}
                        size={20}
                        className="text-muted-foreground"
                      />
                    </button>
                  </td>
                </tr>
                {expandedRows?.has(actor?.id) && (
                  <tr className="bg-muted/30">
                    <td colSpan="9" className="py-4 px-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-card-foreground mb-2">Actor Details</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-muted-foreground">Experience: <span className="text-card-foreground font-medium">{actor?.yearsExperience || 0} years</span></p>
                            <p className="text-muted-foreground">Specializations: <span className="text-card-foreground font-medium">{actor?.specializations?.join(', ') || 'None'}</span></p>
                            <p className="text-muted-foreground">Total Assignments: <span className="text-card-foreground font-medium">{actor?.totalAssignments || 0}</span></p>
                            <p className="text-muted-foreground">Completed Assignments: <span className="text-card-foreground font-medium">{actor?.completedAssignments || 0}</span></p>
                          </div>
                        </div>
                        <div>
                          <h4 className="font-semibold text-card-foreground mb-2">Performance Insights</h4>
                          <div className="space-y-1 text-sm">
                            <p className="text-muted-foreground">Lines per Session: <span className="text-card-foreground font-medium">{actor?.recordingSessions > 0 ? Math.round(actor?.totalLinesRecorded / actor?.recordingSessions) : 0}</span></p>
                            <p className="text-muted-foreground">Availability: <span className={`font-medium ${actor?.isAvailable ? 'text-success' : 'text-error'}`}>{actor?.isAvailable ? 'Available' : 'Unavailable'}</span></p>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformanceTable;